import moment from 'moment'
import qs from 'qs'
import { CalendarV2 } from '@luxuryescapes/contract-svc-calendar'
import { connectionBuildCancellationPolicies } from '@luxuryescapes/lib-refunds'

import request from 'api/requestUtils'
import { arrayToObject, groupBy, min, split, sum } from 'lib/array/arrayUtils'
import { addDays, dateIsBefore, isoDateFormat } from 'lib/datetime/dateUtils'
import { getPackageUniqueKey } from 'lib/offer/offerUtils'
import generateOccupancyStringByRoom from 'lib/offer/generateOccupancyStringByRoom'
import { MONTH_NAMES } from 'constants/dates'
import { isBundleOffer } from 'lib/offer/offerTypes'
import config from 'constants/config'
import { isEnabledForFeature } from 'lib/config/featureFlagUtils'
import { OFFER_TYPE_VILLA } from 'constants/offer'

const FLIGHT_MAX_DAYS_IN_FUTURE = 310
const maxFlightDate = moment().add(FLIGHT_MAX_DAYS_IN_FUTURE, 'days').toDate()

function getFormatData(value: any[], maps: { [key: number]: CalendarV2.Maps[] }): CalendarV2.RateMaps {
  return maps[value.length]?.reduce((acc, key, index) => {
    acc[key] = value[index]
    return acc
  }, {} as CalendarV2.RateMaps)
}

export function mapCalendar(
  serverCalendar: CalendarV2.Package,
  packages: Array<App.Package>,
  offer: App.Offer | App.OfferSummary,
  regionCode: string,
): Array<App.Calendar> {
  const isPriceTaxExclusive = isEnabledForFeature(config.TAX_EXCLUSIVE_REGIONS, regionCode)
  const flightCacheActive = serverCalendar.flightCacheActive
  const flightsNotAvailable = serverCalendar.flightsNotAvailable
  const offerPackage = packages.find(pkg => pkg.id === serverCalendar.packageId)

  const dates = Object.keys(serverCalendar.dates)

  // current issue with server where returns an empty prices array if no avaialble dates
  if (dates.length === 0) {
    return [{
      packageId: serverCalendar.packageId,
      // @ts-ignore: what happens if offerPackage is undefined
      uniqueKey: offerPackage.uniqueKey,
      months: [],
      cheapestDay: undefined,
      cheapestDayWithFlights: undefined,
      flightCacheActive,
      flightsNotAvailable,
    }]
  }

  const mapsData = serverCalendar.maps.reduce((acc, item) => {
    acc[Object.keys(item).length] = item
    return acc
  }, {})

  return serverCalendar.rates.map<App.Calendar>((roomRateId, index) => {
    const pricesByMonth = groupBy(dates, (day) => {
      const currentDay = new Date(day)
      return `${currentDay.getUTCMonth() + 1}-${currentDay.getUTCFullYear()}`
    }, (day) => ({ day, value: serverCalendar.dates[day][index] }))

    const uniqueKey = getPackageUniqueKey(serverCalendar.packageId, serverCalendar.duration, roomRateId)

    const months = [...pricesByMonth.entries()].map<App.CalendarMonth>(([monthKey, pricesInfo]) => {
      const day1Date = new Date(pricesInfo[0].day)
      const monthIndex = day1Date.getUTCMonth()
      const monthText = MONTH_NAMES[monthIndex]
      const yearNum = day1Date.getUTCFullYear()
      const yearText = yearNum.toString()

      const days = pricesInfo.map<App.CalendarDay>((pricesData) => {
        const dayRate = getFormatData(pricesData.value, mapsData)

        const currentDay = new Date(pricesData.day)
        const checkOut = addDays(currentDay, serverCalendar.duration)

        const availableRooms = dayRate.availableRooms
        const blackout = 'blackout' in dayRate ? dayRate.blackout : false
        const prices = 'prices' in dayRate ? dayRate.prices : []
        const price = prices.length > 0 ? sum(prices) : 0
        const hotelValue = 'value' in dayRate ? dayRate.value : 0
        const flightPrice = 'flightPrice' in dayRate ? dayRate.flightPrice : null
        const taxesAndFees = 'taxesAndFees' in dayRate ? dayRate.taxesAndFees : 0
        const propertyFees = 'propertyFees' in dayRate ? dayRate.propertyFees : 0
        const surcharge = 'surcharge' in dayRate ? dayRate.surcharge : 0
        const extraGuestSurcharge = 'extraGuestSurcharge' in dayRate ? dayRate.extraGuestSurcharge : 0
        const dealId = 'dealId' in dayRate ? dayRate.dealId : null
        const hasTactical = 'hasTactical' in dayRate ? dayRate.hasTactical : false

        let hotelAvailable = !!price

        if (offer.bundledWithFlightsOnly) {
          // bundled with flight deals *must* have a flight price if the cache is active
          // otherwise they are considered sold out (because no flight price = no flights)
          hotelAvailable = hotelAvailable && (!flightCacheActive || (flightCacheActive && !!flightPrice))
        }

        const canRequestDates = !hotelAvailable && !blackout &&
          !!offerPackage?.allowDatesRequest && dateIsBefore(checkOut, new Date(offer.bookByDate))

        const hotelPrice = !hotelAvailable ? 0 : isPriceTaxExclusive ? price - taxesAndFees : price

        let roomPrices = prices

        if (isPriceTaxExclusive && prices.length > 0) {
          const part = Math.floor(taxesAndFees / prices.length)
          const residue = taxesAndFees - (part * prices.length)
          roomPrices = roomPrices.map((roomPrice, index) => roomPrice - part - (index + 1 === roomPrices.length ? residue : 0))
        }

        return {
          uniqueKey,
          day: currentDay.getUTCDate(),
          month: monthText,
          year: currentDay.getUTCFullYear().toString(),
          monthKey,
          checkIn: pricesData.day,
          checkOut: isoDateFormat(checkOut),
          canRequestDates,
          taxesAndFees,
          propertyFees,
          roomPrices,
          hotelPrice,
          hotelValue,
          hotelTotal: hotelPrice + surcharge + extraGuestSurcharge + propertyFees,
          packagedTotal: hotelPrice + surcharge + extraGuestSurcharge + (flightPrice ?? 0) + propertyFees,
          extraGuestSurcharge,
          flightPrice: (flightPrice ?? 0),
          surcharge,
          hotelAvailable,
          flightCacheActive,
          roomRateId,
          // @ts-ignore: what happens if offerPackage is undefined
          roomId: offerPackage.roomType.id,
          flightsAvailable: dateIsBefore(checkOut, maxFlightDate),
          availableRooms,
          dealId,
          hasTactical,
        }
      })

      return {
        uniqueKey,
        key: monthKey,
        month: monthText,
        year: yearText,
        yearNum,
        monthIndex,
        days,
        cheapestDay: min(days.filter(d => d.hotelAvailable), d => d.hotelTotal),
        cheapestDayWithFlights: min(days.filter(d => d.flightsAvailable && d.hotelAvailable), d => d.packagedTotal),
        hotelAvailable: days.some(d => d.hotelAvailable),
        canRequestDates: days.some(d => d.canRequestDates),
        flightsAvailable: days.some(d => d.flightsAvailable && d.hotelAvailable),
        flightCacheActive,
      }
    })

    return {
      packageId: serverCalendar.packageId,
      uniqueKey,
      months,
      // @ts-ignore: Array.filter removes all undefined days
      cheapestDay: min(months.map(m => m.cheapestDay).filter(Boolean), d => d.hotelTotal),
      // @ts-ignore: Array.filter removes all undefined days
      cheapestDayWithFlights: min(months.map(m => m.cheapestDayWithFlights).filter(Boolean), d => d.packagedTotal),
      flightCacheActive,
      flightsNotAvailable,
    }
  })
}

interface RequestCalendarParams {
  offer: App.Offer | App.OfferSummary,
  flightOrigin?: string;
  bundledOfferIds?: string[];
  region: string;
  durations: number[];
  minDate?: string;
  packages: Array<App.Package>;
  occupants: Array<App.Occupants>;
  enquiryType?: 'customer' | 'admin'
}

function requestCalendar(params: RequestCalendarParams) {
  const packageIds = params.packages.map((pkg) => pkg.id)
  const query = qs.stringify({
    flightOrigin: params.flightOrigin,
    region: params.region,
    nights: params.durations,
    occupancy: params.occupants.map(generateOccupancyStringByRoom),
    minDate: params.minDate,
    packageIds: packageIds.join(','),
    enquiryType: params.enquiryType,
    bundledOfferIds: params.bundledOfferIds ? params.bundledOfferIds.join(',') : undefined,
  })

  // compression in header for fix the compression on backend side
  // TODO: after fix remove it and use Accept-Encoding
  return request.get<App.ApiResponse<CalendarV2.Package[]>>(`/api/v2/calendar/${params.offer.id}/rates?${query}`, { headers: { compression: 'gzip' } })
    .then((response) => {
      return response.result.flatMap(serverCalendar => mapCalendar(
        serverCalendar,
        params.packages,
        params.offer,
        params.region,
      ))
    })
}

export interface CalendarParams {
  flightOrigin?: string;
  flightProvider?: string;
  minDate?: string;
  packages: Array<App.Package>;
  occupants: Array<App.Occupants>;
  enquiryType?: 'customer' | 'admin'
}

export function getCalendars(
  offer: App.Offer | App.OfferSummary,
  region: string,
  params: CalendarParams = { occupants: [], packages: [], enquiryType: 'customer' },
) {
  if (isBundleOffer(offer)) {
    const bundledOffer = params.packages.reduce((acc, pkg) => {
      acc[pkg.offerId] = pkg.duration
      return acc
    }, {})

    return requestCalendar({
      offer,
      flightOrigin: params.flightOrigin,
      region,
      durations: Object.values(bundledOffer),
      minDate: params.minDate,
      packages: params.packages,
      occupants: params.occupants,
      enquiryType: params.enquiryType,
      bundledOfferIds: Object.keys(bundledOffer),
    })
  } else {
    const queryGroups = groupBy(params.packages, pkg => pkg.duration)

    return Promise.all([...queryGroups.entries()].flatMap(([duration, pkgGroup]) => {
      const chunks = split<App.Package>(pkgGroup, 10)

      return chunks.flatMap((chunk) => {
        return requestCalendar({
          offer,
          flightOrigin: params.flightOrigin,
          region,
          durations: [duration],
          minDate: params.minDate,
          packages: chunk,
          occupants: params.occupants,
          enquiryType: params.enquiryType,
        })
      })
    })).then((calendarArrays) => calendarArrays.flat())
  }
}

interface AvailablePriceParams {
  hideExcludesFlights?: boolean;
  timezone: string;
  regionCode: string;
  currencyCode: string;
}

function availablePriceMap(serverOfferBestPrice: CalendarV2.Availability, options: AvailablePriceParams) {
  const isPriceTaxExclusive = isEnabledForFeature(config.TAX_EXCLUSIVE_REGIONS, options.regionCode)
  const taxesAndFees = serverOfferBestPrice.taxesAndFees ?? 0
  const propertyFees = serverOfferBestPrice.propertyFees ?? 0
  const prices = serverOfferBestPrice.prices
  const price = sum(serverOfferBestPrice.prices)

  const hotelPrice = isPriceTaxExclusive ? price - taxesAndFees : price
  const hotelValue = serverOfferBestPrice.value
  const flightPrice = serverOfferBestPrice.flightPrice
  const surcharge = serverOfferBestPrice.surcharge ?? 0
  const extraGuestSurcharge = serverOfferBestPrice.extraGuestSurcharge ?? 0

  let roomPrices = prices

  if (isPriceTaxExclusive && prices.length > 0) {
    const part = Math.floor(taxesAndFees / prices.length)
    const residue = taxesAndFees - (part * prices.length)
    roomPrices = roomPrices.map((roomPrice, index) => roomPrice - part - (index + 1 === roomPrices.length ? residue : 0))
  }

  return {
    offerId: serverOfferBestPrice.offerId,
    duration: serverOfferBestPrice.duration,
    hotelPrice,
    roomPrices,
    hotelValue,
    price: hotelPrice + surcharge,
    extraGuestSurcharge,
    flightPrice,
    surcharge,
    taxesAndFees,
    propertyFees,
    availableRooms: serverOfferBestPrice.availableRooms,
    packageId: serverOfferBestPrice.packageId,
    // this will make it match our own unique keys
    packageUniqueKey: getPackageUniqueKey(serverOfferBestPrice.packageId, serverOfferBestPrice.duration, serverOfferBestPrice.roomRateId),
    roomRateId: serverOfferBestPrice.roomRateId,
    cancellationPolicies: serverOfferBestPrice.connection ? connectionBuildCancellationPolicies(serverOfferBestPrice.connection.cancellationPolicies, options) : undefined,
    hasTactical: serverOfferBestPrice.hasTactical,
  } as App.OfferAvailableRate
}

interface AvailabilityQueryParams extends AvailablePriceParams {
  offerType?: string;
  offerIds: string[];
  checkIn: string;
  checkOut: string;
  rooms: Array<App.Occupants>;
  dynamic?: boolean;
  lowestPrices?: boolean;
  flightOrigin?: string;
  bundledOfferId?: string;
}

export function getAvailableRatesForOffer(params: AvailabilityQueryParams): Promise<App.OfferAvailableRate[]> {
  const queryParams = qs.stringify({
    offerIds: params.offerIds.join(','),
    region: params.regionCode,
    checkIn: params.checkIn,
    checkOut: params.checkOut,
    occupancy: params.rooms.map(generateOccupancyStringByRoom),
    dynamic: params.dynamic,
    lowestPrices: params.lowestPrices,
    flightOrigin: params.flightOrigin,
    bundledOfferIds: params.bundledOfferId,
  })

  // compression in header for fix the compression on backend side
  // TODO: after fix remove it and use Accept-Encoding
  try {
    return request.get<App.ApiResponse<Array<CalendarV2.Availability>>>(`/api/v2/calendar/availability?${queryParams}`, { headers: { compression: 'gzip' } })
      .then(resp => resp.result.map(result => availablePriceMap(result, {
        hideExcludesFlights: params.offerType === OFFER_TYPE_VILLA,
        timezone: params.timezone,
        regionCode: params.regionCode,
        currencyCode: params.currencyCode,
      })))
  } catch (error) {
    if (error && typeof error === 'object' && 'status' in error &&
        (error.status === 404 || error.status === 422)) {
      // Offer has no packages available with these parameters
      return Promise.resolve([])
    }
    throw error
  }
}

interface LowestPriceQueryParams extends AvailablePriceParams {
  offerType: string;
  offerIds: string[];
  checkIn: string;
  checkOut: string;
  rooms: Array<App.Occupants>;
  bundledOfferId?: string;
}

export async function getLowestPriceForOffer(params: LowestPriceQueryParams) {
  const data = await getAvailableRatesForOffer({ ...params, lowestPrices: true, dynamic: false, offerType: params.offerType })

  const rate = data[0]

  if (!rate) {
    return { available: false }
  }

  return ({
    available: true,
    rate,
  })
}

type ValidCalendar = Omit<App.Calendar, 'uniqueKey'> & { uniqueKey: string }
function isValidCalendar(calendar: App.Calendar): calendar is ValidCalendar {
  return Boolean(calendar.uniqueKey)
}
export async function getCalendarsByOccupancy(
  offer: App.Offer | App.OfferSummary,
  region: string,
  params: CalendarParams,
) {
  const calendars = await getCalendars(offer, region, params)
  const validCalendars = calendars.filter(isValidCalendar)
  return arrayToObject(validCalendars, (pkgCalendar) => pkgCalendar.uniqueKey)
}
